// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace INumberFormat {
  export type INegativeType = 'right' | 'left' | 'brackets' | 'none' | '';
}

export interface INumberFormat {
  negativeType?: INumberFormat.INegativeType;
  negativeLeftOut?: boolean;
  negativeLeftPos?: number;
  negativeRightOut?: boolean;
  negativeRightPos?: number;
  negativeLeftSymbol?: string;
  negativeRightSymbol?: string;
  suffix?: string;
  prefix?: string;
  absMask?: string;
  decimal?: string;
  decimalChar?: string;
  integerSeparator?: string;
  decimalsSeparator?: string;
  noSeparator?: boolean;
  allowedSeparators?: string[];
  padLeft?: number;
  maxLeft?: number;
  padRight?: number;
  maxRight?: number;
  round?: number;
  truncate?: number;
  noUnits?: boolean;
}

export interface IFormat extends INumberFormat {
  (number: number | string, overrideOptions?: INumberFormat): string;
  unformat?: (number: string, allowedSeparators?: string[]) => string;
}

// where x is already the integer part of the number
function addIntegerSeparators(x: string, separator: string) {
  x += '';

  if (!separator) {
    return x;
  }

  const rgx = /(\d+)(\d{3})/;

  while (rgx.test(x)) {
    x = x.replace(rgx, '$1' + separator + '$2');
  }

  return x;
}

// where x is already the decimal part of the number
function addDecimalSeparators(x: string, separator: string) {
  x += '';

  if (!separator) {
    return x;
  }

  const rgx = /(\d{3})(\d+)/;

  while (rgx.test(x)) {
    x = x.replace(rgx, '$1' + separator + '$2');
  }

  return x;
}

// where x is the integer part of the number
function padLeft(x: string, padding: number) {
  x = x + '';

  const buf: string[] = [];

  while (buf.length + x.length < padding) {
    buf.push('0');
  }

  return buf.join('') + x;
}

// where x is the decimals part of the number
function padRight(x: string, padding: number) {
  if (x) {
    x += '';
  }
  else {
    x = '';
  }

  const buf: string[] = [];

  while (buf.length + x.length < padding) {
    buf.push('0');
  }

  return x + buf.join('');
}

function truncate(x: string, length: number) {
  if (x) {
    x += '';
  }

  if (x && x.length > length) {
    return x.substr(0, length);
  }
  else {
    return x;
  }
}

// where number is an array with 0th item as integer string and 1st item as decimal string (no negatives)
function round(numberParts: string[], places: number) {
  if (numberParts[1] && places >= 0 && numberParts[1].length > places) {
    // truncate to correct number of decimal places
    let decim = numberParts[1].slice(0, places);

    // if next digit was >= 5 we need to round up
    if (+(numberParts[1].substr(places, 1)) >= 5) {
      // But first count leading zeros as converting to a number will loose them
      let leadingzeros = '';

      while (decim.charAt(0) === '0') {
        leadingzeros = leadingzeros + '0';
        decim = decim.substr(1);
      }

      // Then we can change decim to a number and add 1 before replacing leading zeros
      decim = (+decim + 1) + '';
      decim = leadingzeros + decim;

      if (decim.length > places) {
        // adding one has made it longer
        numberParts[0] = (+numberParts[0] + +decim.charAt(0)) + ''; // add value of firstchar to the integer part
        decim = decim.substring(1); // ignore the 1st char at the beginning which is the carry to the integer part
      }
    }

    numberParts[1] = decim;
  }

  return numberParts;
}

export default function formatter(options: INumberFormat): IFormat {
  options = options || {};

  // *********************************************************************************************
  // Set defaults for negatives
  // options.negative, options.negativeOut, options.separator retained for backward compatibility
  // *********************************************************************************************

  // type of negative; default left
  options.negativeType = options.negativeType || 'left';

  // negative symbols '-' or '()'
  if (typeof options.negativeLeftSymbol !== 'string') {
    switch (options.negativeType) {
      case 'left':
        options.negativeLeftSymbol = '-';
        break;
      case 'brackets':
        options.negativeLeftSymbol = '(';
        break;
      default:
        options.negativeLeftSymbol = '';
    }
  }

  if (typeof options.negativeRightSymbol !== 'string') {
    switch (options.negativeType) {
      case 'right':
        options.negativeRightSymbol = '-';
        break;
      case 'brackets':
        options.negativeRightSymbol = ')';
        break;
      default:
        options.negativeRightSymbol = '';
    }
  }

  // whether negative symbol should be inside/outside prefix and suffix

  if (typeof options.negativeLeftOut !== 'boolean') {
    options.negativeLeftOut = true;
  }

  if (typeof options.negativeRightOut !== 'boolean') {
    options.negativeRightOut = true;
  }

  // prefix and suffix
  options.prefix = options.prefix || '';
  options.suffix = options.suffix || '';

  // separators
  if (typeof options.integerSeparator !== 'string') {
    options.integerSeparator = ',';
  }

  options.decimalsSeparator = typeof options.decimalsSeparator === 'string' ? options.decimalsSeparator : '';
  options.decimal = options.decimal || '.';

  // padders
  options.padLeft = options.padLeft || -1; // default no padding
  options.padRight = options.padRight || -1; // default no padding

  if (typeof options.prefix !== 'string') {
    options.prefix = '';
  }

  const format: IFormat = (number: number | string, overrideOptions?: INumberFormat) => {
    overrideOptions = overrideOptions || {};

    if (number || number === 0) {
      number = '' + number; // convert number to string if it isn't already
    }
    else {
      return '';
    }

    // identify a negative number and make it absolute
    const output: (string[] | string)[] = [];
    const negative = number.charAt(0) === '-';
    number = number.replace(/^-/g, '');

    // Prepare output with left hand negative and/or prefix
    if (!options.negativeLeftOut && !overrideOptions.noUnits) {
      output.push(options.prefix);
    }

    if (negative) {
      output.push(options.negativeLeftSymbol);
    }

    if (options.negativeLeftOut && !overrideOptions.noUnits) {
      output.push(options.prefix);
    }

    // Format core number
    const numberParts = number.split('.');

    if (options.round != null) {
      round(numberParts, options.round);
    }

    if (options.truncate != null) {
      numberParts[1] = truncate(numberParts[1], options.truncate);
    }

    if (options.padLeft && options.padLeft > 0) {
      numberParts[0] = padLeft(numberParts[0], options.padLeft);
    }

    if (options.padRight && options.padRight > 0) {
      numberParts[1] = padRight(numberParts[1], options.padRight);
    }

    if (!overrideOptions.noSeparator && numberParts[1]) {
      numberParts[1] = addDecimalSeparators(numberParts[1], options.decimalsSeparator);
    }

    if (!overrideOptions.noSeparator && numberParts[0]) {
      numberParts[0] = addIntegerSeparators(numberParts[0], options.integerSeparator);
    }

    output.push(numberParts[0]);

    if (numberParts[1]) {
      output.push(options.decimal);
      output.push(numberParts[1]);
    }

    // Prepare output with right hand negative and/or prefix
    if (options.negativeRightOut && !overrideOptions.noUnits) {
      output.push(options.suffix);
    }

    if (negative) {
      output.push(options.negativeRightSymbol);
    }

    if (!options.negativeRightOut && !overrideOptions.noUnits) {
      output.push(options.suffix);
    }

    // join output and return
    return output.join('');
  };

  format.negativeType = options.negativeType;
  format.negativeLeftOut = options.negativeLeftOut;
  format.negativeLeftSymbol = options.negativeLeftSymbol;
  format.negativeRightOut = options.negativeRightOut;
  format.negativeRightSymbol = options.negativeRightSymbol;
  format.prefix = options.prefix;
  format.suffix = options.suffix;
  format.integerSeparator = options.integerSeparator;
  format.decimalsSeparator = options.decimalsSeparator;
  format.decimal = options.decimal;
  format.padLeft = options.padLeft;
  format.padRight = options.padRight;
  format.truncate = options.truncate;
  format.round = options.round;

  function unformat(number: string, allowedSeparators?: string[]) {
    allowedSeparators = allowedSeparators || [];

    if (options.allowedSeparators) {
      options.allowedSeparators.forEach((s) => (allowedSeparators).push(s));
    }

    allowedSeparators.push(options.integerSeparator);
    allowedSeparators.push(options.decimalsSeparator);
    number = number.replace(options.prefix, '');
    number = number.replace(options.suffix, '');

    let newNumber = number;

    do {
      number = newNumber;

      for (const value of allowedSeparators) {
        newNumber = newNumber.replace(value, '');
      }
    } while (newNumber !== number);

    return number;
  }

  format.unformat = unformat;

  return format;
}
